import Grid from '@blocks/Grid'
import MainContainer from '@blocks/MainContainer'
import { route } from '@helpers'
import useGallery from '@hooks/useGallery'
import useTitle from "@hooks/useTitle"
import AcceptanceSlider from '@jury/components/sliders/AcceptanceSlider'
import useSalon from '@jury/models/Salon/useSalon'
import { desktop } from '@styles/mixins'
import { colors } from '@styles/vars'
import useMainTemplate from '@templates/Main/hooks/useMainTemplate'
import React from 'react'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import SalonAcceptancesDashboard from './components/Dashboard/SalonAcceptancesDashboard'
import SalonAcceptancesAwards from './components/SalonAcceptancesAwards'
import SalonAcceptancesSections from './components/SalonAcceptancesSections'
import SalonAcceptancesSort, { sortByScore } from './components/SalonAcceptancesSort'
import SalonAcceptancesContext from './context'
import useCountAcceptancesByCustomer from './hooks/useCountAcceptancesByCustomer'
import useSalonAcceptancesArchive from './hooks/useSalonAcceptancesArchive'

export default function SalonAcceptancesPage() {
    const { id } = useParams<{ id: string }>()
    const salon = useSalon(id)
    const acceptances = salon.api.acceptances.use(`jury:salon:${id}:acceptances`)

    useTitle(`Salon: ${salon.attrs.name()}` )
    useMainTemplate({ category: "jury:salons" })

    const archive = useSalonAcceptancesArchive(acceptances.data)

    // HANDLERS
    const handleAcceptanceChange = async (acceptance: Jury.Resources.Acceptance) => {
        acceptances.setData(old => updateAcceptance(old, acceptance))
    }

    archive.sort(sortByScore, true).use()

    const gallery = useGallery(archive.data || [], AcceptanceSlider)
    const countAcceptancesByCustomer = useCountAcceptancesByCustomer(acceptances.data)

    if (!salon.state) 
        return <MainContainer heading={<><Link to={route("jury")}>Salons</Link> — {salon.attrs.name()}</>} />

    return (
        <SalonAcceptancesContext.Provider value={{ countAcceptancesByCustomer, gallery, salon, handleAcceptanceChange } as Jury.Contexts.SalonAcceptances}>
            <MainContainer heading={<><Link to={route("jury")}>Salons</Link> — {salon.attrs.name()}</>}>
                <Grid gap="20px">
                    <SalonAcceptancesDashboard acceptances={acceptances.data || []} />
                    <Header>
                        <SalonAcceptancesSections sections={salon.state.sections} archive={archive} />
                        <SalonAcceptancesAwards awards={salon.state.awards} archive={archive} />
                        <SalonAcceptancesSort archive={archive} />
                    </Header>
                    {archive.element}
                </Grid>
            </MainContainer>
        </SalonAcceptancesContext.Provider>
    )
}

const Header = styled.div`
    background: ${colors.white};
    border: 1px solid ${colors.border};
    padding: 15px;
    border-radius: 10px;

    & > *:not(:last-child) {
        margin-bottom: 20px;
    }

    ${desktop(css`
        display: flex;
        justify-content: space-between;
        align-items: center;

        & > *:not(:last-child) {
            margin-bottom: 0;
            margin-right: 20px;
        }
    `)}
`

// FUNCTIONS
const updateAcceptance = (data: Jury.Resources.Acceptance[] | undefined, acceptance: Jury.Resources.Acceptance) => {
    return data?.map(item => {
        if (item.id !== acceptance.id) 
            return item
            
        return acceptance
    })
}